/**
 * Which languages Kontora is written in, and how a request for one is answered.
 *
 * German first and English second (`project.md` §4). German is not merely one
 * of two options: it is what somebody gets when their own language is not among
 * them, because the product is DACH-first.
 *
 * The resolution chain here is the same one the server applies in
 * `domain.ResolveLocale`, and `locales.test.ts` runs the same table of cases the
 * Go test does. Two implementations of one rule is normally a smell; here it is
 * unavoidable — the browser has to pick a language before it has asked the API
 * anything at all — so the answer is to pin them against each other rather than
 * to hope.
 */

export const LOCALES = ["de-DE", "en-GB"] as const;

export type Locale = (typeof LOCALES)[number];

/** What "we do not have your language" resolves to. */
export const FALLBACK_LOCALE: Locale = "de-DE";

/** True for a locale Kontora's interface actually exists in. */
export function isLocale(value: string | undefined): value is Locale {
  return LOCALES.includes(value as Locale);
}

/**
 * Answers which language to render for somebody who asked for `want`.
 *
 * Exact match, then any locale sharing the language, then German. So an en-US
 * browser gets English and an fr-FR one gets German — never a blank interface,
 * which is the only outcome that would actually be wrong.
 */
export function resolveLocale(want: string | undefined | null): Locale {
  const asked = (want ?? "").trim();
  if (isLocale(asked)) return asked;

  const language = asked.split("-")[0]?.toLowerCase();
  if (language) {
    const sameLanguage = LOCALES.find((have) => languageOf(have) === language);
    if (sameLanguage) return sameLanguage;
  }
  return FALLBACK_LOCALE;
}

/** The language half of a locale tag, lower-cased: "de-DE" is "de". */
function languageOf(locale: string): string {
  return (locale.split("-")[0] ?? locale).toLowerCase();
}

/**
 * The language to use before the API has been asked.
 *
 * `navigator.languages` is in the reader's own order of preference, so the first
 * entry Kontora can honour wins. This is only ever the starting point: the
 * moment `/me` answers, the stored choice takes over, because a person who chose
 * English on a German laptop meant it.
 */
export function browserLocale(): Locale {
  const preferences =
    typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]);

  for (const preference of preferences) {
    if (isLocale(preference)) return preference;
    const language = languageOf(preference ?? "");
    const sameLanguage = LOCALES.find((have) => languageOf(have) === language);
    if (sameLanguage) return sameLanguage;
  }
  return FALLBACK_LOCALE;
}

/** What each language calls itself, for a language picker. */
export const LOCALE_NAMES: Record<Locale, string> = {
  "de-DE": "Deutsch",
  "en-GB": "English",
};
